import { useRef } from 'react';
import type { ReactNode } from 'react';
import { cx } from '@/shared/ui/cx';

export type SegmentedOption<T extends string> = {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
};

export type SegmentedProps<T extends string> = {
  /** Names the group for assistive tech. */
  label: string;
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
  className?: string;
};

/**
 * Source / Style / Active-line style — a small set of mutually exclusive
 * choices, always visible.
 *
 * Uses the tablist pattern with `aria-selected`, matching the existing
 * `.segmented` control that the current tests already query as
 * `getByRole('tab', ...)`.
 */
export function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
  size = 'md',
  className,
}: SegmentedProps<T>) {
  const currentIndex = options.findIndex((o) => o.value === value);
  const listRef = useRef<HTMLDivElement>(null);

  // Arrow keys move between tabs; that is expected of the role and free to add.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!delta) return;
    event.preventDefault();

    const enabled = options.filter((o) => !o.disabled);
    if (enabled.length === 0) return;
    const pos = enabled.findIndex((o) => o.value === options[currentIndex]?.value);
    const next = enabled[(pos + delta + enabled.length) % enabled.length];
    onChange(next.value);

    /* Focus has to travel with the selection. `tabIndex` is 0 on the selected
       tab and -1 on the rest, so changing the selection without moving focus
       strands it on a tab that is now unselected *and* untabbable: screen
       readers announce the wrong one, and the next Tab leaves from the wrong
       place. Indexing into the rendered tabs rather than the `enabled` array
       because disabled options still render and still occupy a DOM slot. */
    const tabs = listRef.current?.querySelectorAll<HTMLElement>('[role="tab"]');
    tabs?.[options.findIndex((o) => o.value === next.value)]?.focus();
  };

  return (
    <div
      ref={listRef}
      className={cx('segmented', `segmented--${size}`, className)}
      role="tablist"
      aria-label={label}
      onKeyDown={handleKeyDown}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={selected}
            disabled={option.disabled}
            tabIndex={selected ? 0 : -1}
            className={cx('segmented__tab', selected && 'is-selected')}
            onClick={() => onChange(option.value)}
          >
            {option.icon}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
